import { Button, Input } from "@biteship/atom";
import { ArrowPathIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { generateWebhookAuthToken, updateCancelShipmentUrl, updateCreateShipmentUrl, updateGetRatesUrl } from "@store/action/openCourier";
import { useCallback, useEffect, useState } from "react";
import WebhookResult from "./WebhookResult";

/**
 * 
 * @param {Object} props
 * @param {string} props.authToken - webhook authentication token
 * @param {string} props.openCourierId - open courier id
 * @param {string} props.ratesUrl - url for get rates api
 * @param {string} props.createShipmentUrl - url for create shipment api
 * @param {string} props.cancelShipmentUrl - url for cancel shipment api
 * @returns 
 */
export default function Webhooks({
  authToken,
  openCourierId,
  ratesUrl,
  createShipmentUrl,
  cancelShipmentUrl,
}) {
  const [updatedRatesUrl, setUpdatedRatesUrl] = useState("");
  const [updatedCreateShipmentUrl, setUpdatedCreateShipmentUrl] = useState("");
  const [updatedCancelShipmentUrl, setUpdatedCancelShipmentUrl] = useState("");
  const [updatedAuthToken, setUpdatedAuthToken] = useState("");

  const initialWebhookResultState = {
    requestBody: null,
    responseBody: null,
    httpStatusCode: null,
    responseTime: null,
    responseTimeUnit: null,
    httpMethod: null,
  };
  const [getRatesWebhookResult, setGetRatesWebhookResult] = useState(initialWebhookResultState);
  const [createShipmentWebhookResult, setCreateShipmentWebhookResult] = useState(initialWebhookResultState);
  const [cancelShipmentWebhookResult, setCancelShipmentWebhookResult] = useState(initialWebhookResultState);

  useEffect(() => {
    setUpdatedRatesUrl(ratesUrl);
    setUpdatedCreateShipmentUrl(createShipmentUrl);
    setUpdatedCancelShipmentUrl(cancelShipmentUrl);
    setUpdatedAuthToken(authToken);
  }, [ratesUrl, createShipmentUrl, cancelShipmentUrl, authToken]);

  const handleSaveRatesUrl = useCallback(() => {
    updateGetRatesUrl(openCourierId, updatedRatesUrl)
      .then((result) => {
        alert("Rates API berhasil disimpan");
        setGetRatesWebhookResult(result.webhookResult);
      }).catch((error) => {
        alert(error?.response?.data?.error || "Rates API gagal disimpan");
      });
  }, [ratesUrl, updatedRatesUrl]);

  const handleSaveShipmentUrl = useCallback(() => {
    updateCreateShipmentUrl(openCourierId, updatedCreateShipmentUrl)
      .then((result) => {
        alert("Create Order API berhasil disimpan");
        setCreateShipmentWebhookResult(result.webhookResult);
      }).catch((error) => {
        alert(error?.response?.data?.error || "Create Order API gagal disimpan");
      });
  }, [createShipmentUrl, updatedCreateShipmentUrl]);

  const handleSaveCancelShipmentUrl = useCallback(() => {
    updateCancelShipmentUrl(openCourierId, updatedCancelShipmentUrl)
      .then((result) => {
        alert("Cancel Order API berhasil disimpan");
        setCancelShipmentWebhookResult(result.webhookResult);
      }).catch((error) => {
        alert(error?.response?.data?.error || "Cancel Order API gagal disimpan");
      });
  }, [cancelShipmentUrl, updatedCancelShipmentUrl]);

  const handleCopyToken = useCallback(() => {
    if (updatedAuthToken) {
      navigator.clipboard.writeText(updatedAuthToken);
      alert("Token berhasil disalin");
    }
  }, [updatedAuthToken]);

  const handleGenerateTokenClick = useCallback(() => {
    if (!window.confirm("Apakah anda yakin ingin generate webhook authentication token baru?")) return;

    generateWebhookAuthToken(openCourierId)
      .then((token) => {
        setUpdatedAuthToken(token);
        alert("Token berhasil di generate");
      }).catch((error) => {
        alert(error?.response?.data?.error || "Token gagal di generate");
      });
  }, [openCourierId]);

  return (
    <div className="space-y-4 w-full mb-12">
      <h2 className="text-sm text-gray-900 font-semibold mb-0">Berikan Response ke Biteship</h2>

      <div className="flex space-x-4 w-full md:w-2/3 items-end">
        <Input
          label={<span>URL untuk terima Get Rates API <span className="text-gray-700">(POST Method)</span></span>}
          value={updatedRatesUrl}
          onChange={(e) => setUpdatedRatesUrl(e.target.value)}
          className="w-full text-gray-900"
        />
        <Button 
          title="Tes & Simpan"
          className="w-fit whitespace-nowrap"
          onClick={handleSaveRatesUrl}
        />
      </div>
      {(getRatesWebhookResult.requestBody || getRatesWebhookResult.responseBody || getRatesWebhookResult.httpStatusCode) && (
        <div className="w-full md:w-2/3">
          <WebhookResult 
            webhookResult={getRatesWebhookResult} 
            authToken={updatedAuthToken}
            title="Get Rates Webhook Result"
          />
        </div>
      )}

      <div className="flex space-x-4 w-full md:w-2/3 items-end">
        <Input
          label={<span>URL untuk terima Create Order API <span className="text-gray-700">(POST Method)</span></span>}
          value={updatedCreateShipmentUrl}
          onChange={(e) => setUpdatedCreateShipmentUrl(e.target.value)}
          className="w-full text-gray-900"
        />
        <Button 
          title="Tes & Simpan"
          className="w-fit whitespace-nowrap"
          onClick={handleSaveShipmentUrl}
        />
      </div>
      {(createShipmentWebhookResult.requestBody || createShipmentWebhookResult.responseBody || createShipmentWebhookResult.httpStatusCode) && (
        <div className="w-full md:w-2/3">
          <WebhookResult 
            webhookResult={createShipmentWebhookResult} 
            authToken={updatedAuthToken}
            title="Create Shipment Webhook Result"
          />
        </div>
      )}

      <div className="flex space-x-4 w-full md:w-2/3 items-end">
        <Input
          label={<span>URL untuk terima Cancel Order API <span className="text-gray-700">(POST Method)</span></span>}
          value={updatedCancelShipmentUrl}
          onChange={(e) => setUpdatedCancelShipmentUrl(e.target.value)}
          className="w-full text-gray-900"
        />
        <Button 
          title="Tes & Simpan"
          className="w-fit whitespace-nowrap"
          onClick={handleSaveCancelShipmentUrl}
        />
      </div>
      {(cancelShipmentWebhookResult.requestBody || cancelShipmentWebhookResult.responseBody || cancelShipmentWebhookResult.httpStatusCode) && (
        <div className="w-full md:w-2/3">
          <WebhookResult 
            webhookResult={cancelShipmentWebhookResult} 
            authToken={updatedAuthToken}
            title="Cancel Shipment Webhook Result"
          />
        </div>
      )}

      <div>
        <div className="flex space-x-4 w-full md:w-2/3 items-end">
          { updatedAuthToken ? (
            <>
              <Input
                label="Webhook Authentication Token"
                value={updatedAuthToken}
                className="w-full"
                disabled
              />
              <div className="flex space-x-2">
                <Button 
                  title={<ArrowPathIcon className="w-6 h-6" />}
                  className={`h-fit justify-center md:w-auto py-2.5`}
                  onClick={handleGenerateTokenClick}
                  secondary
                />
                <Button 
                  title={<DocumentDuplicateIcon className="w-6 h-6" />}
                  className="h-fit justify-center md:w-auto py-2.5"
                  onClick={handleCopyToken}
                />
              </div>
            </>
          ) : (
            <Button 
              title={"Generate Authentication Token"}
              className={`h-fit justify-center md:w-auto`}
              onClick={handleGenerateTokenClick}
            />
          )}
        </div>
        <p className="text-sm text-gray-700 mt-2">Biteship akan mengirimkan token ini pada request headers <code className="text-xs bg-gray-300 p-1 rounded">Authorization</code>.</p>
      </div>
      
    </div>
  )
}